/**
 * Lane C0-PROPS: the plains building kit (docs/design/C0_TOWN_LAYOUT.md §2, build step 3). Each structure the 256 m
 * plains lays (packages/shared plainsMap.ts: `type` = the nearest kit type, `name` = the layout's Thai name) is drawn
 * as itself on its full footprint, instead of as the nearest square sprite (layoutLook.ts).
 *
 * On only for the 256 m plains (`setKitMap`, called as each map's scenery is built: passes/kitProps.ts) and unless
 * `localStorage['c0.kit'] = '0'` (for before/after shots). Every painter draws its faces through buildings.ts `wall` /
 * `top` / `roof` (noted by `faceRec`), so route C rotation (apps/c0-rotate) can turn them.
 */
import { hash2, type GameMap, type Structure } from '@nine/shared';
import type { Sprite } from '../scenery';
import { northGate, paifang } from './gates';
import { bannerHall, bellTower, gardenPagoda, inn, operaStage, ruinedTemple, shrine, tent, trainingHall, waysidePavilion } from './halls';
import { campGate, drumTower, logWatchtower, moonGate, thatchCottage, timberTownhouse } from './extra';
import { courtyardHouse, jail, longHouse, plainHouse, shophouse, stable, stiltHouse, warehouse } from './homes';
import type { RoofColour } from './parts';
import { stall } from './stalls';

const off = (() => {
  try {
    return localStorage.getItem('c0.kit') === '0';
  } catch {
    return false;
  }
})();

let active = false;

/** Whether a map is one the kit dresses (the 256 m plains). */
export const kitMap = (m: GameMap): boolean => !off && m.zone === 'plains' && m.w === 256;

/** Set as a map's scenery is built: the kit draws that map's structures (or not). */
export function setKitMap(m: GameMap): void {
  active = kitMap(m);
}
/** Testing and review sheets: switch the kit on or off directly. */
export function setKitActive(on: boolean): void {
  active = on;
}

/** The plains' look variants back to roof colours (plainsMap.ts `ROOF_VARIANT`: red 2, grey 0, green and wood 1). */
const colourOf = (v: number): RoofColour => (v === 2 ? 'red' : v === 0 ? 'grey' : 'green');

const SHOPS = new Set(['หมอยา', 'ร้านผ้า', 'ตำรา', 'ตีเหล็ก']);

/** The painter for a structure, or null when the kit does not draw it. */
function painter(st: Structure): (() => Sprite) | null {
  const { w, h, name = '' } = st;
  const c = colourOf(st.variant);
  const v = Math.floor(hash2(st.i, st.j) * 6);
  switch (st.type) {
    case 'hall':
      return name === 'วิหารร้าง' ? () => ruinedTemple(w, h, c) : () => bannerHall(w, h, c);
    case 'guard':
      return name === 'คุก' ? () => jail(w, h, c) : () => trainingHall(w, h, c);
    case 'paifang':
      return () => paifang(Math.max(w, h), 'red');
    case 'gate':
      if (name === 'ประตูพระจันทร์') return () => moonGate(Math.max(w, h), c);
      if (name === 'ประตูค่ายโจร') return () => campGate(Math.max(w, h));
      return () => northGate(Math.max(w, h), c);
    case 'inn':
      return () => inn(w, h, 'red');
    case 'shed':
      return () => operaStage(w, h, 'red');
    case 'pagoda':
      return () => gardenPagoda(Math.min(w, h), c);
    case 'tower':
      if (name === 'หอกลอง') return () => drumTower(w, h, c);
      if (name === 'หอสังเกตการณ์ไม้') return () => logWatchtower(w, h);
      return () => bellTower(w, h, c);
    case 'stable':
      return () => stable(w, h, c);
    case 'stall':
      return () => stall(w, h, name, v);
    case 'kiosk':
      return () => shrine(w, h, c, name === 'ศาลเจ้าสุสาน');
    case 'pavilion':
      return () => waysidePavilion(Math.min(w, h), c, v);
    case 'tent':
      return () => tent(Math.min(w, h), v);
    case 'house':
      if (name === 'โรงคลัง') return () => warehouse(w, h, c);
      if (SHOPS.has(name)) return () => shophouse(w, h, c, name, v);
      if (name === 'บ้านมีลานใน') return () => courtyardHouse(w, h, c);
      if (name === 'เรือนยาว') return () => longHouse(w, h, c);
      if (name === 'เรือนใต้ถุนสูง') return () => stiltHouse(w, h, c);
      if (name === 'กระท่อมมุงจาก') return () => thatchCottage(w, h, v);
      if (name === 'เรือนไม้สองชั้น') return () => timberTownhouse(w, h, c);
      return () => plainHouse(w, h, c, v);
    default:
      return null;
  }
}

/** Whether the kit draws this structure now (it then keeps its full footprint: layoutLook.ts). */
export function kitDraws(st: Structure): boolean {
  return active && !!st.type && painter(st) !== null;
}

/** The kit's sprite for a structure (cached by its look), or null when the kit does not draw it. */
export function kitSprite(st: Structure, cache: Map<string, Sprite>): Sprite | null {
  if (!active || !st.type) return null;
  const p = painter(st);
  if (!p) return null;
  const v = Math.floor(hash2(st.i, st.j) * 6);
  const key = `kit:${st.type}:${st.name ?? ''}:${st.w}x${st.h}:${st.variant}:${v}`;
  let s = cache.get(key);
  if (!s) cache.set(key, (s = p()));
  return s;
}

/** Review sheets: the painter for a structure regardless of the switch. */
export function kitPaint(st: Structure): Sprite | null {
  return painter(st)?.() ?? null;
}
